import React, {
  Suspense, lazy, useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Fade, Snackbar, Typography } from '@material-ui/core';
import {
  first, get, getOr, toNumber, toString,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Grid, Spinner } from '../../common/components';
import * as actions from '../actions';
import {
  DeleteConfirmDialog,
  IndividualInfo,
  IndividualPagination,
  ResponseDate,
} from '../components';
import messages from '../messages';

const IndividualPDFViewer = lazy(() => import(/* webpackChunkName: 'IndividualPDFViewer', webpackPrefetch: true */ '../components/IndividualPDFViewer'));

const S = {
  Paragraph: styled(Typography)`
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Row: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 8px 0 16px;
  `,
  IndividualInfo: styled(IndividualInfo)`
    margin-bottom: 16px;
  `,
};

const Individual = ({
  fileUrl, match, newsletter, survey,
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [value, setValue] = useState('1');
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const individuals = useSelector(state => state.newsletter.individuals);
  const responseDeleted = useSelector(state => state.newsletter.responseDeleted);

  const dispatch = useDispatch();

  const openDialog = useCallback(() => setOpen(true), []);
  const closeDialog = useCallback(() => setOpen(false), []);
  const deleteIndividualResponse = useCallback(() => dispatch(actions.deleteIndividualResponseRequest({
    answers: get(`[${pageNumber - 1}].answers`, individuals.data),
    gcid: get(`[${pageNumber - 1}].id`, individuals.data),
    nid: match.params.nid,
    sid: match.params.sid,
  })), [dispatch, pageNumber, individuals.data, match.params.nid, match.params.sid]);
  const moveToPrevPage = useCallback(() => {
    setPageNumber(prevPageNumber => (prevPageNumber === 1 ? 1 : prevPageNumber - 1));

    setValue(toString(pageNumber === 1 ? 1 : pageNumber - 1));
  }, [pageNumber]);
  const moveToNextPage = useCallback(() => {
    setPageNumber(prevPageNumber => (
      prevPageNumber < getOr(0, 'recordCount', newsletter.data)
        ? prevPageNumber + 1 : prevPageNumber
    ));

    setValue(toString(pageNumber < getOr(0, 'recordCount', newsletter.data)
      ? pageNumber + 1 : pageNumber));
  }, [newsletter.data, pageNumber]);
  const handleBlur = useCallback(() => setValue(toString(pageNumber)), [pageNumber]);
  const handleChange = useCallback((event) => {
    if ((/^\d+$/.test(event.target.value) && first(event.target.value) !== '0')
      || !event.target.value) {
      setValue(event.target.value);
    }
  }, []);
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      if (toNumber(value) > getOr(0, 'recordCount', newsletter.data)) {
        setPageNumber(getOr(1, 'recordCount', newsletter.data));

        setValue(toString(getOr(1, 'recordCount', newsletter.data)));
      } else if (!value) {
        setPageNumber(1);

        setValue('1');
      } else {
        setPageNumber(toNumber(value));

        setValue(value);
      }
    }
  }, [newsletter.data, value]);

  const handleClose = () => setDialogOpen(false);

  const handleEnter = () => {
    setErrorMessage('삭제에 실패하였습니다.');
  };

  const handleExited = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    dispatch(actions.fetchIndividualsRequest({ nid: match.params.nid, sid: match.params.sid }));
  }, [dispatch, match.params.nid, match.params.sid]);

  useEffect(() => {
    if (responseDeleted.error) {
      setDialogOpen(true);

      dispatch(actions.initResponseDeleted());
    }
  }, [dispatch, responseDeleted.error]);

  return (
    <>
      <Grid item xs={6} />
      <Grid item xs={6}>
        {get('recordCount', newsletter.data)
          ? (
            <>
              <S.Row>
                <IndividualPagination
                  pageNumber={pageNumber}
                  recordCount={get('recordCount', newsletter.data)}
                  value={value}
                  moveToPrevPage={moveToPrevPage}
                  moveToNextPage={moveToNextPage}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                />
                <ResponseDate updatedAt={get(`[${pageNumber - 1}].updatedAt`, individuals.data)} />
              </S.Row>
              <S.IndividualInfo
                target={get(`[${pageNumber - 1}].target`, individuals.data)}
                openDialog={openDialog}
              />
              <Suspense fallback={<Spinner />}>
                <IndividualPDFViewer
                  fileUrl={fileUrl}
                  individual={get(pageNumber - 1, individuals.data)}
                  survey={survey}
                />
              </Suspense>
            </>
          )
          : (
            <S.Paragraph>
              <FormattedMessage {...messages.individualParagraphNoResponse} />
            </S.Paragraph>
          )}
      </Grid>
      <DeleteConfirmDialog
        target={get(`[${pageNumber - 1}].target`, individuals.data)}
        open={open}
        onClose={closeDialog}
        onDelete={deleteIndividualResponse}
      />
      {responseDeleted.loading && <Spinner />}
      <Snackbar
        autoHideDuration={3500}
        open={dialogOpen}
        onClose={handleClose}
        onEnter={handleEnter}
        onExited={handleExited}
        message={errorMessage}
        TransitionComponent={Fade}
      />
    </>
  );
};

Individual.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      nid: PropTypes.string.isRequired,
      sid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  newsletter: PropTypes.shape({
    data: PropTypes.shape({
      recordCount: PropTypes.number.isRequired,
    }),
  }).isRequired,
  survey: PropTypes.shape({
    data: PropTypes.shape({
      questions: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.oneOf(['SINGLE_SELECTION', 'MULTIPLE_SELECTION', 'DESCRIPTIVE']),
        number: PropTypes.number,
        content: PropTypes.string,
        required: PropTypes.bool,
        position: PropTypes.shape({
          x: PropTypes.number,
          y: PropTypes.number,
        }),
        size: PropTypes.shape({
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      })),
    }),
    signatures: PropTypes.arrayOf(PropTypes.shape({
      number: PropTypes.number,
      position: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
      }),
    })),
  }).isRequired,
};

export default Individual;
